import { readRemembered, writeRemembered } from '../../../shared/lib/remembered';

/**
 * Вход в Google для выгрузок «Статистики».
 *
 * Раньше токен жил в памяти и просился на каждую область отдельно: окно
 * входа выскакивало после каждой перезагрузки, раз в час и на каждый вид
 * отчёта (Word→Docs, Docs, Таблицы). Теперь один токен сразу на все три
 * области, он лежит в sessionStorage до истечения, а повторный вход идёт без
 * выбора аккаунта (`prompt: ''` + подсказка с почтой прошлого входа).
 *
 * Область одна — drive.file, «только файлы, созданные этим приложением»
 * (04.11.2026). Раньше просили ещё documents и spreadsheets — это доступ ко
 * всем документам и таблицам человека, включая личные; сотрудники справедливо
 * пугались, а Google из-за «чувствительных» областей показывал «приложение не
 * проверено» и держал потолок в 100 человек. Docs и Sheets API принимают
 * drive.file для файлов, которые приложение создало само, а все выгрузки
 * именно создают новый файл и пишут только в него.
 */
export const DRIVE_FILE = 'https://www.googleapis.com/auth/drive.file';
export const GOOGLE_SCOPES = [DRIVE_FILE] as const;

/** Старые области, которые ещё передают выгрузки, — теперь их покрывает drive.file. */
const COVERED_BY_DRIVE_FILE = new Set([
  'https://www.googleapis.com/auth/documents',
  'https://www.googleapis.com/auth/spreadsheets',
]);

export function effectiveScope(scope: string): string {
  return COVERED_BY_DRIVE_FILE.has(scope) ? DRIVE_FILE : scope;
}
const SESSION_KEY = 'if:google-token';
const EMAIL_KEY = 'google:email';

export type CachedToken = { value: string; expiresAt: number; scopes: string[] };

let gisLoaded = false;
let memoryToken: CachedToken | null = null;

async function loadGis(): Promise<void> {
  if (gisLoaded) return;
  await new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.onload = () => {
      gisLoaded = true;
      resolve();
    };
    script.onerror = () => reject(new Error('Не удалось загрузить Google Identity Services'));
    document.head.appendChild(script);
  });
}

/** Годный токен для области: не истёк и область действительно выдана. */
export function usableToken(token: CachedToken | null, scope: string, now: number): string | null {
  if (!token || now >= token.expiresAt) return null;
  return token.scopes.includes(scope) ? token.value : null;
}

/** Ответ Google → запись кэша. Минута запаса, чтобы токен не истёк посреди выгрузки. */
export function tokenFromResponse(
  response: { access_token: string; expires_in: number; scope?: string },
  requested: readonly string[],
  now: number,
): CachedToken {
  // Пользователь может снять галочку с части областей: верим ответу, а не запросу.
  const scopes = response.scope ? response.scope.split(/\s+/).filter(Boolean) : [...requested];
  return { value: response.access_token, expiresAt: now + (response.expires_in - 60) * 1000, scopes };
}

/**
 * Google ответил 401 — ключ отозван раньше срока (сменили режим приложения,
 * человек снял доступ в аккаунте). Срок в кэше этого не знает, поэтому ключ
 * выбрасываем: следующее нажатие откроет вход и получит новый. Повторить
 * запрос сразу нельзя — окно входа без клика браузер заблокирует.
 */
export function staleGoogleTokenError(status: number): Error | null {
  if (status !== 401) return null;
  memoryToken = null;
  try {
    sessionStorage.removeItem(SESSION_KEY);
  } catch {
    // нет хранилища — в памяти уже сброшено
  }
  return new Error('Вход в Google устарел — нажмите выгрузку ещё раз и войдите заново');
}

function readSession(): CachedToken | null {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    return raw ? (JSON.parse(raw) as CachedToken) : null;
  } catch {
    return null;
  }
}

function writeSession(token: CachedToken): void {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(token));
  } catch {
    // Нет хранилища — токен останется в памяти до перезагрузки.
  }
}

/** Почта прошлого входа — подсказка Google, чтобы не выбирать аккаунт заново. */
async function rememberEmail(token: CachedToken): Promise<void> {
  if (!token.scopes.includes(DRIVE_FILE)) return;
  try {
    const res = await fetch('https://www.googleapis.com/drive/v3/about?fields=user(emailAddress)', {
      headers: { Authorization: `Bearer ${token.value}` },
    });
    if (!res.ok) return;
    const email = ((await res.json()) as { user?: { emailAddress?: string } }).user?.emailAddress;
    if (email) writeRemembered(EMAIL_KEY, email);
  } catch {
    // Подсказка необязательна.
  }
}

const SCOPE_NAMES: Record<string, string> = {
  [DRIVE_FILE]: 'Google Диск (файлы этого приложения)',
};

export async function getAccessToken(requestedScope: string): Promise<string> {
  const scope = effectiveScope(requestedScope);
  const now = Date.now();
  memoryToken ??= readSession();
  const cached = usableToken(memoryToken, scope, now);
  if (cached) return cached;

  await loadGis();

  const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
  if (!clientId) throw new Error('VITE_GOOGLE_CLIENT_ID не задан в .env');

  // Просим все области разом (и ту, что нужна сейчас, если её нет в списке).
  const requested = GOOGLE_SCOPES.includes(scope as (typeof GOOGLE_SCOPES)[number])
    ? [...GOOGLE_SCOPES]
    : [...GOOGLE_SCOPES, scope];
  const hint = readRemembered<string | null>(EMAIL_KEY, null) ?? undefined;

  return new Promise<string>((resolve, reject) => {
    const client = window.google.accounts.oauth2.initTokenClient({
      client_id: clientId,
      scope: requested.join(' '),
      // Пустой prompt — согласие спрашивается, только если его ещё не давали.
      prompt: '',
      hint,
      include_granted_scopes: true,
      callback: (response) => {
        if (response.error) {
          reject(new Error(`Google OAuth ошибка: ${response.error}`));
          return;
        }
        const token = tokenFromResponse(response, requested, Date.now());
        memoryToken = token;
        writeSession(token);
        void rememberEmail(token);
        if (!token.scopes.includes(scope)) {
          reject(new Error(`Google не дал доступ к «${SCOPE_NAMES[scope] ?? scope}» — разрешите его в окне входа`));
          return;
        }
        resolve(token.value);
      },
      error_callback: (error) => {
        reject(new Error(error.type === 'popup_closed' ? 'Окно входа Google закрыто' : `Google OAuth ошибка: ${error.type}`));
      },
    });
    client.requestAccessToken();
  });
}
